import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RequestService } from './htttp/request.service';

export interface AccessCodePurchaseRequest {
  numberOfSubscriptions: number;
  purchaserName: string;
  districtOrganization?: string;
  email: string;
}

export interface AccessCodePurchaseResponse {
  data: {
    purchaseId: string;
    totalAmount: number;
    numberOfSubscriptions: number;
  };
  message: string;
  code: number;
}

export interface AccessCodeCheckoutSessionRequest {
  purchaseId: string;
}

export interface AccessCodeCheckoutSessionResponse {
  data: {
    sessionUrl: string;
    sessionId: string;
  };
  message: string;
  code: number;
}

export interface AccessCodePurchaseDetails {
  id: string;
  numberOfSubscriptions?: number;
  purchaserName?: string;
  districtOrganization?: string;
  email: string;
  stripeSessionId?: string;
  stripePriceId?: string;
  totalAmount?: number;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  accessCodes: Array<{
    code: string;
    isUsed: boolean;
    usedBy?: string;
    usedAt?: Date;
  }>;
  expiresAt?: Date;
  createdAt: Date;
  updatedAt?: Date;
}

export interface AccessCodePurchaseDetailsResponse {
  data: AccessCodePurchaseDetails;
  message: string;
  code: number;
}

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  // Base URL is already handled by RequestService
  private apiPath = '/v1/subscription/access-code';

  constructor(private requestService: RequestService) {}

  /**
   * Create a new access code purchase
   */
  createAccessCodePurchase(purchaseData: AccessCodePurchaseRequest): Observable<AccessCodePurchaseResponse> {
    // Use local backend for testing
    return this.requestService.post<AccessCodePurchaseResponse>(`${this.apiPath}/purchase`, purchaseData);
  }

  /**
   * Create a Stripe checkout session for access code purchase
   */
  createAccessCodeCheckoutSession(checkoutData: AccessCodeCheckoutSessionRequest): Observable<AccessCodeCheckoutSessionResponse> {
    // Use local backend for testing
    return this.requestService.post<AccessCodeCheckoutSessionResponse>(`${this.apiPath}/checkout-session`, checkoutData);
  }

  /**
   * Get access code purchase details
   */
  getAccessCodePurchase(purchaseId: string): Observable<AccessCodePurchaseDetailsResponse> {
    // Use local backend for testing
    return this.requestService.get<AccessCodePurchaseDetailsResponse>(`${this.apiPath}/purchase/${purchaseId}`);
  }

  /**
   * Get access code purchase details by session ID
   */
  getAccessCodePurchaseBySession(sessionId: string): Observable<AccessCodePurchaseDetailsResponse> {
    return this.requestService.get<AccessCodePurchaseDetailsResponse>(`${this.apiPath}/purchase-by-session/${sessionId}`);
  }

  /**
   * Test if the access code purchase system is working
   */
  testAccessCodeSystem(): Observable<any> {
    return this.requestService.get(`${this.apiPath}/test`);
  }

  /**
   * Complete access code purchase flow
   * This method combines creating purchase and checkout session
   */
  completeAccessCodePurchase(purchaseData: AccessCodePurchaseRequest): Observable<AccessCodeCheckoutSessionResponse> {
    return new Observable(observer => {
      this.createAccessCodePurchase(purchaseData).subscribe({
        next: purchaseResponse => {
          if (!purchaseResponse?.data?.purchaseId) {
            observer.error(new Error('Invalid purchase response: missing purchaseId'));
            return;
          }

          const checkoutData: AccessCodeCheckoutSessionRequest = {
            purchaseId: purchaseResponse.data.purchaseId,
          };

          this.createAccessCodeCheckoutSession(checkoutData).subscribe({
            next: checkoutResponse => {
              if (!checkoutResponse?.data?.sessionUrl) {
                observer.error(new Error('Invalid checkout response: missing sessionUrl'));
                return;
              }
              observer.next(checkoutResponse);
              observer.complete();
            },
            error: error => observer.error(error),
          });
        },
        error: error => observer.error(error),
      });
    });
  }
}
